import React, { useState, useEffect } from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { PageHeader, AppButton } from "../ui";
import { Code2 } from "lucide-react";
import TestCaseList from "./TestCaseList";
import TestCaseForm from "./TestCaseForm";
import TagSelector from "./TagSelector";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import { fetchJudgeLanguages, toCategoryOptions } from '../../utils/judgeLanguages';

const DIFFICULTIES = ["Easy", "Medium", "Hard"];

const emptyQuestion = {
  title: "",
  description: "",
  categories: [],
  labels: [],
  difficulty: "",
  sampleCode: {},
  solution: {},
  testCases: [],
  isPublic: false,
};

const QuestionForm = ({ question, onSave, onCancel, mode = "institute" }) => {
  const [form, setForm] = useState(emptyQuestion);
  const [labelsInput, setLabelsInput] = useState("");
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: form.description,
    onUpdate({ editor }) {
      setForm(prev => ({ ...prev, description: editor.getHTML() }));
    },
  });

  useEffect(() => {
    fetchJudgeLanguages()
      .then(raw => setCategoryOptions(toCategoryOptions(raw)))
      .finally(() => setLoadingCategories(false));
  }, []);

  useEffect(() => {
    if (question) {
      setForm({
        ...emptyQuestion,
        ...question,
        sampleCode: question.sampleCode || {},
        solution: question.solution || {},
        testCases: question.testCases || [],
        categories: question.categories || [],
        labels: question.labels || [],
      });
      setLabelsInput(question.labels ? question.labels.join(", ") : "");
    } else {
      setForm(emptyQuestion);
      setLabelsInput("");
    }
  }, [question]);

  useEffect(() => {
  if (editor && form.description !== editor.getHTML()) {
    editor.commands.setContent(form.description || "");
  }
  // eslint-disable-next-line
}, [form.description, editor]);

  const handleLabelsChange = (e) => {
    const input = e.target.value;
    setLabelsInput(input);
    setForm((prev) => ({
      ...prev,
      labels: input
        .split(",")
        .map((l) => l.trim())
        .filter(Boolean),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoriesChange = (selected) => {
    setForm((prev) => {
      const newSampleCode = {};
      const newSolution = {};
      const categoriesWithIds = selected.map(name => {
        // Check if already an object (from existing form data)
        if (typeof name === 'object' && name.name) return name;
        const opt = categoryOptions.find(o => (typeof o === 'string' ? o : o.value) === name);
        return { id: opt?.id, name };
      });
      selected.forEach((lang) => {
        const langName = typeof lang === 'object' ? lang.name : lang;
        newSampleCode[langName] = prev.sampleCode[langName] || "";
        newSolution[langName] = prev.solution[langName] || "";
      });
      return {
        ...prev,
        categories: categoriesWithIds,
        sampleCode: newSampleCode,
        solution: newSolution,
      };
    });
  };

  const handleCodeChange = (lang, type, value) => {
    setForm((prev) => ({
      ...prev,
      [type]: { ...prev[type], [lang]: value },
    }));
  };

  // Handlers for test case modal
  const handleAddTestCase = () => {
    setEditingTestCase(null);
    setShowTestCaseModal(true);
  };
  const handleEditTestCase = (tc) => {
    setEditingTestCase(tc);
    setShowTestCaseModal(true);
  };
  const handleDeleteTestCase = (tcId) => {
    setForm((prev) => ({
      ...prev,
      testCases: prev.testCases.filter((t) => t._id !== tcId),
    }));
  };
  const handleSaveTestCase = (tc) => {
    const isEditing = !!editingTestCase;
    setForm((prev) => {
      const prevCases = prev.testCases || [];
      let newList;
      if (isEditing) {
        newList = prevCases.map((t) =>
          String(t._id) === String(tc._id) ? tc : t
        );
      } else {
        newList = [...prevCases, { ...tc, _id: Date.now().toString() }];
      }
      return { ...prev, testCases: newList };
    });
    setShowTestCaseModal(false);
    setEditingTestCase(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const labels = labelsInput
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean);

    // Remove _id from testCases before sending to backend
    const cleanedTestCases = (form.testCases || []).map(({ _id, ...rest }) => rest);

    const payload = {
      ...form,
      labels,
      testCases: cleanedTestCases,
    };
    // Only include isPublic when this form is being used in the platform
    // library context — institute users must never set this flag.
    if (mode !== "library") delete payload.isPublic;

    onSave(payload);
  };


  return (
    <div className="cqb-form-page">
      <PageHeader
        icon={Code2}
        title={question && question._id ? "Edit Coding Question" : "Add Coding Question"}
        description={question && question._id ? "Update the question details below" : "Fill in the details to add a new coding question"}
        backButton
        onBack={onCancel}
      />
      <Form onSubmit={handleSubmit} className="question-form">
        {/* Basic Information */}
        <div className="cqb-form-section">
          <h6 className="cqb-form-section-title">Basic Information</h6>
          <Row className="gy-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select
                  name="difficulty"
                  value={form.difficulty}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                {loadingCategories ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner size="sm" animation="border" />
                    <span className="text-muted small">Loading languages…</span>
                  </div>
                ) : (
                  <TagSelector
                    options={categoryOptions}
                    value={(form.categories || []).map(c => typeof c === 'object' ? c.name : c)}
                    onChange={handleCategoriesChange}
                    label="Categories"
                    placeholder="Select tech stacks"
                    multiple
                    className="coding-question-category-selector"
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Labels (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  value={labelsInput}
                  onChange={handleLabelsChange}
                  placeholder="e.g., Sliding Window, DP, Two Pointers"
                />
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {form.labels.map((l) => (
                    <span className="cqb-tag cqb-tag--label" key={l}>
                      {l}
                    </span>
                  ))}
                </div>
              </Form.Group>
            </Col>
            {mode === "library" && (
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="cqb-is-public"
                    label="Make this question public — visible to every institute"
                    checked={!!form.isPublic}
                    onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
        </div>

        {/* Description */}
        <div className="cqb-form-section">
          <h6 className="cqb-form-section-title">Description</h6>
          <Form.Group>
            <div className="cqb-editor-wrap">
              <div className="cqb-editor-toolbar">
                <AppButton
                  size="sm"
                  variant={editor?.isActive('bold') ? 'default' : 'secondary'}
                  onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}
                >
                  <b>B</b>
                </AppButton>
                <AppButton
                  size="sm"
                  variant={editor?.isActive('italic') ? 'default' : 'secondary'}
                  onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}
                >
                  <i>I</i>
                </AppButton>
                <AppButton
                  size="sm"
                  variant={editor?.isActive('bulletList') ? 'default' : 'secondary'}
                  onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBulletList().run(); }}
                >
                  • List
                </AppButton>
                <AppButton
                  size="sm"
                  variant={editor?.isActive('orderedList') ? 'default' : 'secondary'}
                  onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleOrderedList().run(); }}
                >
                  1. List
                </AppButton>
              </div>
              <EditorContent editor={editor} className="cqb-editor-content" value={form.description} />
            </div>
          </Form.Group>
        </div>

        {/* Code Templates */}
        {(form.categories || []).length > 0 && (
          <div className="cqb-form-section">
            <h6 className="cqb-form-section-title">Code Templates</h6>
            <Row className="gy-3">
              {(form.categories || []).map((cat) => {
                const lang = typeof cat === 'object' ? cat.name : cat;
                return (
                <React.Fragment key={lang}>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Sample Code ({lang})</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={(form.sampleCode || {})[lang] || ""}
                        onChange={(e) => handleCodeChange(lang, "sampleCode", e.target.value)}
                        placeholder={`Enter sample code for ${lang}`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Solution ({lang}) - optional</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={(form.solution || {})[lang] || ""}
                        onChange={(e) => handleCodeChange(lang, "solution", e.target.value)}
                        placeholder={`Enter solution code for ${lang}`}
                      />
                    </Form.Group>
                  </Col>
                </React.Fragment>
                );
              })}
            </Row>
          </div>
        )}

        {/* Test Cases */}
        <div className="cqb-form-section">
          <TestCaseList
            testCases={form.testCases || []}
            onAdd={handleAddTestCase}
            onEdit={handleEditTestCase}
            onDelete={handleDeleteTestCase}
          />
        </div>

        {/* Footer Actions */}
        <div className="cqb-form-footer">
          {onCancel && (
            <AppButton type="button" variant="outline" onClick={onCancel}>
              Cancel
            </AppButton>
          )}
          <AppButton type="submit">
            {question ? "Save Changes" : "Add Question"}
          </AppButton>
        </div>
      </Form>
      <TestCaseForm
        show={showTestCaseModal}
        onHide={() => { setShowTestCaseModal(false); setEditingTestCase(null); }}
        onSave={handleSaveTestCase}
        testCase={editingTestCase}
      />
    </div>
  );
};

export default QuestionForm;
